import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { getTagColor } from "@/lib/constants";
import type { Tag } from "@/api/types";

interface TagBadgeProps {
  tag: Tag;
  /** When provided, renders an X button on the right that calls onRemove. */
  onRemove?: () => void;
  size?: "sm" | "md";
  className?: string;
}

export function TagBadge({
  tag,
  onRemove,
  size = "sm",
  className,
}: TagBadgeProps) {
  const colors = getTagColor(tag.color);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-medium",
        colors.pillBg,
        colors.pillText,
        colors.pillBorder,
        size === "sm"
          ? "px-2 py-0.5 text-xs"
          : "px-2.5 py-1 text-sm",
        className,
      )}
    >
      <span
        className={cn(
          "shrink-0 rounded-full",
          colors.dot,
          size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2",
        )}
      />
      <span className="truncate">{tag.name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove tag ${tag.name}`}
          className={cn(
            "rounded-full transition-colors hover:bg-black/5",
            size === "sm" ? "ml-0.5 p-0.5" : "ml-1 p-0.5",
          )}
        >
          <X
            className={cn(
              "opacity-70",
              size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5",
            )}
          />
        </button>
      )}
    </span>
  );
}

interface TagSwatchProps {
  color: string;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
}

/** Small colored circle used by the color picker grid. */
export function TagSwatch({ color, selected, onClick, className }: TagSwatchProps) {
  const colors = getTagColor(color);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Pick color ${color}`}
      aria-pressed={selected}
      className={cn(
        "h-6 w-6 rounded-full transition-all",
        colors.swatch,
        selected
          ? "ring-2 ring-slate-900 ring-offset-2"
          : "hover:scale-110",
        className,
      )}
    />
  );
}
